import React from 'react';
import { Loader2, Paperclip, Upload } from 'lucide-react';

import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from '../ui/sonner';
import { documentsAPI } from '../../lib/api';

/**
 * Dépôt d'une pièce **rattachée au trajet**, sans quitter l'écran.
 *
 * C'était le trou du produit: l'écran `/documents` archivait bien le fichier,
 * mais sans `associated_trip_id`. La pièce existait, le trajet continuait de
 * la réclamer, et le seul chemin qui débloquait vraiment quelque chose était
 * l'application du chauffeur. Un exploitant assis à son bureau, le scan sous
 * les yeux, n'avait aucun moyen de s'en servir.
 *
 * `documentsAPI.upload()` accepte des métadonnées depuis toujours; ce
 * composant est le deuxième appelant à s'en servir (le premier est l'état des
 * lieux de location), et le premier à passer le trajet.
 */

/** Les types qu'un exploitant dépose depuis un trajet, dans son vocabulaire. */
const TYPES_PIECE = [
  { value: 'DELIVERY_NOTE', label: 'Bon de livraison' },
  { value: 'WAYBILL', label: 'Lettre de voiture / BL' },
  { value: 'CUSTOMS_DECLARATION', label: 'Déclaration douanière' },
  { value: 'RECEIPT', label: 'Reçu / ticket' },
  { value: 'INVOICE', label: 'Facture' },
  { value: 'OTHER', label: 'Autre pièce' },
];

const TAILLE_MAX = 10 * 1024 * 1024;

export default function TeleverseurEnLigne({
  tripId,
  defaultDocType = 'DELIVERY_NOTE',
  onUploaded,
  label = 'Joindre la pièce à ce trajet',
}) {
  const [docType, setDocType] = React.useState(defaultDocType);
  const [envoiEnCours, setEnvoiEnCours] = React.useState(false);
  const inputRef = React.useRef(null);
  const inputId = React.useMemo(
    () => `televerseur-${tripId || 'trajet'}-${Math.random().toString(36).slice(2, 8)}`,
    [tripId],
  );

  const envoyer = async (fichier) => {
    if (!fichier) return;
    if (fichier.size > TAILLE_MAX) {
      toast.error('Fichier trop lourd — 10 Mo maximum.');
      return;
    }
    setEnvoiEnCours(true);
    try {
      // `associated_trip_id` est la seule raison d'être de ce composant:
      // sans lui, la pièce est archivée mais ne répare rien.
      const document = await documentsAPI.upload(fichier, docType, {
        associated_trip_id: tripId,
        capture_source: 'web_operator',
      });
      toast.success('Pièce jointe au trajet.');
      if (onUploaded) await onUploaded(document);
    } catch (error) {
      toast.error("La pièce n'a pas pu être envoyée. Réessayez.");
    } finally {
      setEnvoiEnCours(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="televerseur-en-ligne">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger className="sm:w-56" data-testid="televerseur-type">
            <SelectValue placeholder="Type de pièce" />
          </SelectTrigger>
          <SelectContent>
            {TYPES_PIECE.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          className="sr-only"
          disabled={envoiEnCours || !tripId}
          data-testid="televerseur-input"
          onChange={(event) => {
            const fichier = event.target.files?.[0];
            // Le champ est vidé tout de suite: sans cela, choisir deux fois le
            // même fichier ne déclencherait rien.
            event.target.value = '';
            envoyer(fichier);
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={envoiEnCours || !tripId}
          onClick={() => inputRef.current?.click()}
          className="gap-2"
        >
          {envoiEnCours ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Envoi en cours…
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" aria-hidden="true" />
              Choisir un fichier
            </>
          )}
        </Button>
      </div>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Paperclip className="h-3 w-3 shrink-0" aria-hidden="true" />
        Photo ou PDF, 10 Mo maximum. La pièce se rattache à ce trajet.
      </p>
    </div>
  );
}
